import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DeleteSweepRoundedIcon from "@mui/icons-material/DeleteSweepRounded";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

const EMPTY_RANGE_DRAFT = {
  weekdays: [1, 2, 3, 4, 5],
  start_time: "08:00",
  end_time: "17:00",
};

export default function ModalSchedules({
  open,
  form,
  setForm,
  saving,
  weekdays,
  onClose,
  onSave,
}) {
  const [rangeDraft, setRangeDraft] = useState(EMPTY_RANGE_DRAFT);
  const [feedback, setFeedback] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  useEffect(() => {
    if (open) {
      setRangeDraft({
        weekdays: [1, 2, 3, 4, 5],
        start_time: "08:00",
        end_time: "17:00",
      });

      setFeedback({
        open: false,
        severity: "success",
        message: "",
      });
    }
  }, [open]);

  const showFeedback = (severity, message) => {
    setFeedback({
      open: true,
      severity,
      message,
    });
  };

  const closeFeedback = () => {
    setFeedback((previous) => ({
      ...previous,
      open: false,
    }));
  };

  const getWeekdayName = (value) => {
    return (
      weekdays.find(
        (weekday) => weekday.value === Number(value)
      )?.label || "Día"
    );
  };

  const toggleWeekday = (weekday) => {
    setRangeDraft((previous) => {
      const selected = previous.weekdays.includes(weekday);

      return {
        ...previous,
        weekdays: selected
          ? previous.weekdays.filter(
              (value) => value !== weekday
            )
          : [...previous.weekdays, weekday].sort(
              (a, b) => a - b
            ),
      };
    });
  };

  const selectWeekdays = (values) => {
    setRangeDraft((previous) => ({
      ...previous,
      weekdays: [...values],
    }));
  };

  const addRanges = () => {
    if (rangeDraft.weekdays.length === 0) {
      showFeedback(
        "error",
        "Selecciona al menos un día."
      );
      return;
    }

    if (
      !rangeDraft.start_time ||
      !rangeDraft.end_time
    ) {
      showFeedback(
        "error",
        "Debes ingresar la hora inicial y final."
      );
      return;
    }

    if (
      rangeDraft.start_time >=
      rangeDraft.end_time
    ) {
      showFeedback(
        "error",
        "La hora final debe ser mayor que la inicial."
      );
      return;
    }

    for (const weekday of rangeDraft.weekdays) {
      const overlaps = form.ranges.some((range) => {
        return (
          Number(range.weekday) ===
            Number(weekday) &&
          rangeDraft.start_time <
            range.end_time &&
          rangeDraft.end_time >
            range.start_time
        );
      });

      if (overlaps) {
        showFeedback(
          "error",
          `El rango se cruza con otro registrado el ${getWeekdayName(
            weekday
          ).toLowerCase()}.`
        );
        return;
      }
    }

    const newRanges =
      rangeDraft.weekdays.map((weekday) => ({
        weekday,
        start_time:
          rangeDraft.start_time,
        end_time:
          rangeDraft.end_time,
        is_active: true,
      }));

    setForm((previous) => ({
      ...previous,
      ranges: [
        ...previous.ranges,
        ...newRanges,
      ].sort((a, b) => {
        if (
          Number(a.weekday) !==
          Number(b.weekday)
        ) {
          return (
            Number(a.weekday) -
            Number(b.weekday)
          );
        }

        return String(
          a.start_time
        ).localeCompare(
          String(b.start_time)
        );
      }),
    }));

    showFeedback(
      "success",
      rangeDraft.weekdays.length === 1
        ? "Rango añadido correctamente."
        : `Rango aplicado a ${rangeDraft.weekdays.length} días.`
    );
  };

  const removeRange = (index) => {
    setForm((previous) => ({
      ...previous,
      ranges:
        previous.ranges.filter(
          (_, rangeIndex) =>
            rangeIndex !== index
        ),
    }));

    showFeedback(
      "success",
      "Ingreso eliminado correctamente."
    );
  };

  const removeAllRanges = () => {
    if (!form.ranges.length) {
      showFeedback(
        "error",
        "No existen ingresos para eliminar."
      );
      return;
    }

    setForm((previous) => ({
      ...previous,
      ranges: [],
    }));

    showFeedback(
      "success",
      "Todos los ingresos fueron eliminados."
    );
  };

  const updateRange = (
    index,
    field,
    value
  ) => {
    setForm((previous) => ({
      ...previous,
      ranges:
        previous.ranges.map(
          (range, rangeIndex) =>
            rangeIndex === index
              ? {
                  ...range,
                  [field]: value,
                }
              : range
        ),
    }));
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            fontSize: {
              xs: 20,
              sm: 22,
            },
          }}
        >
          {form.id
            ? "Editar horario"
            : "Nuevo horario"}
        </DialogTitle>

        <DialogContent>
          <Stack
            spacing={2.5}
            sx={{
              paddingTop: 1,
            }}
          >
            <TextField
              fullWidth
              required
              label="Nombre del horario"
              value={form.name}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  name:
                    event.target.value,
                }))
              }
            />

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Descripción"
              value={form.description}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  description:
                    event.target.value,
                }))
              }
            />

            {form.id && (
              <Box
                sx={{
                  minHeight: 48,
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    "space-between",
                  border:
                    "1px solid #e5e7eb",
                  borderRadius: 2,
                  paddingX: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                  }}
                >
                  Horario activo
                </Typography>

                <Switch
                  checked={
                    form.is_active
                  }
                  onChange={(event) =>
                    setForm(
                      (previous) => ({
                        ...previous,
                        is_active:
                          event.target
                            .checked,
                      })
                    )
                  }
                />
              </Box>
            )}

            <Box>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 800,
                  marginBottom: 1.5,
                }}
              >
                Añadir ingresos
              </Typography>

              <Box
                sx={{
                  padding: {
                    xs: 1.5,
                    sm: 2,
                  },
                  border:
                    "1px solid #d1d5db",
                  borderRadius: 2,
                  backgroundColor:
                    "#fafafa",
                }}
              >
                <Typography
                  sx={{
                    color: "#4b5563",
                    fontSize: 13,
                    fontWeight: 700,
                    marginBottom: 1,
                  }}
                >
                  Selecciona los días
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    marginBottom: 1.5,
                  }}
                >
                  {weekdays.map(
                    (weekday) => {
                      const selected =
                        rangeDraft.weekdays.includes(
                          weekday.value
                        );

                      return (
                        <Button
                          key={
                            weekday.value
                          }
                          size="small"
                          variant={
                            selected
                              ? "contained"
                              : "outlined"
                          }
                          onClick={() =>
                            toggleWeekday(
                              weekday.value
                            )
                          }
                          sx={{
                            minWidth: {
                              xs: "calc(50% - 4px)",
                              sm: 92,
                            },
                            borderBottom:
                              selected
                                ? "3px solid #b00020"
                                : undefined,
                            fontWeight: 700,
                          }}
                        >
                          {
                            weekday.label
                          }
                        </Button>
                      );
                    }
                  )}
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    marginBottom: 2,
                  }}
                >
                  <Button
                    size="small"
                    variant="text"
                    onClick={() =>
                      selectWeekdays([
                        1, 2, 3, 4, 5,
                      ])
                    }
                    sx={{
                      fontWeight: 700,
                    }}
                  >
                    Lunes a viernes
                  </Button>

                  <Button
                    size="small"
                    variant="text"
                    onClick={() =>
                      selectWeekdays([
                        1, 2, 3, 4, 5,
                        6, 7,
                      ])
                    }
                    sx={{
                      fontWeight: 700,
                    }}
                  >
                    Toda la semana
                  </Button>

                  <Button
                    size="small"
                    variant="text"
                    color="inherit"
                    onClick={() =>
                      selectWeekdays([])
                    }
                    sx={{
                      fontWeight: 700,
                    }}
                  >
                    Limpiar
                  </Button>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "1fr 1fr auto",
                    },
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <TextField
                    fullWidth
                    type="time"
                    label="Desde"
                    value={
                      rangeDraft.start_time
                    }
                    onChange={(event) =>
                      setRangeDraft(
                        (previous) => ({
                          ...previous,
                          start_time:
                            event.target
                              .value,
                        })
                      )
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />

                  <TextField
                    fullWidth
                    type="time"
                    label="Hasta"
                    value={
                      rangeDraft.end_time
                    }
                    onChange={(event) =>
                      setRangeDraft(
                        (previous) => ({
                          ...previous,
                          end_time:
                            event.target
                              .value,
                        })
                      )
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />

                  <Button
                    variant="contained"
                    startIcon={
                      <AddRoundedIcon />
                    }
                    onClick={addRanges}
                    sx={{
                      minHeight: 56,
                      whiteSpace:
                        "nowrap",
                      borderBottom:
                        "3px solid #b00020",
                      fontWeight: 700,
                    }}
                  >
                    Aplicar ingreso
                  </Button>
                </Box>
              </Box>
            </Box>

            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: {
                    xs: "stretch",
                    sm: "center",
                  },
                  justifyContent:
                    "space-between",
                  flexDirection: {
                    xs: "column",
                    sm: "row",
                  },
                  gap: 1.2,
                  marginBottom: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 800,
                  }}
                >
                  Ingresos configurados
                </Typography>

                {form.ranges.length >
                  0 && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={
                      <DeleteSweepRoundedIcon />
                    }
                    onClick={
                      removeAllRanges
                    }
                    sx={{
                      fontWeight: 700,
                      borderColor:
                        "#fecaca",
                      color: "#b91c1c",
                      backgroundColor:
                        "#fff",
                      "&:hover": {
                        borderColor:
                          "#ef4444",
                        backgroundColor:
                          "#fef2f2",
                      },
                    }}
                  >
                    Eliminar todos
                  </Button>
                )}
              </Box>

              {form.ranges.length ===
              0 ? (
                <Box
                  sx={{
                    minHeight: 100,
                    display: "flex",
                    alignItems:
                      "center",
                    justifyContent:
                      "center",
                    padding: 2,
                    border:
                      "1px dashed #d1d5db",
                    borderRadius: 2,
                    textAlign:
                      "center",
                  }}
                >
                  <Typography
                    sx={{
                      color:
                        "#6b7280",
                      fontSize: 14,
                      fontWeight: 500,
                    }}
                  >
                    Todavía no se han
                    añadido ingresos.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1.2}>
                  {form.ranges.map(
                    (range, index) => (
                      <Box
                        key={`${range.weekday}-${range.start_time}-${range.end_time}-${index}`}
                        sx={{
                          display:
                            "grid",
                          gridTemplateColumns:
                            {
                              xs: "1fr",
                              sm: "1.3fr 1fr 1fr auto",
                            },
                          alignItems:
                            "center",
                          gap: 1.5,
                          padding: 1.5,
                          border:
                            "1px solid #e5e7eb",
                          borderRadius:
                            2,
                          backgroundColor:
                            "#ffffff",
                        }}
                      >
                        <FormControl
                          fullWidth
                        >
                          <InputLabel>
                            Día
                          </InputLabel>

                          <Select
                            value={
                              range.weekday
                            }
                            label="Día"
                            onChange={(
                              event
                            ) =>
                              updateRange(
                                index,
                                "weekday",
                                Number(
                                  event
                                    .target
                                    .value
                                )
                              )
                            }
                          >
                            {weekdays.map(
                              (
                                weekday
                              ) => (
                                <MenuItem
                                  key={
                                    weekday.value
                                  }
                                  value={
                                    weekday.value
                                  }
                                >
                                  {
                                    weekday.label
                                  }
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </FormControl>

                        <TextField
                          fullWidth
                          type="time"
                          label="Desde"
                          value={
                            range.start_time
                          }
                          onChange={(
                            event
                          ) =>
                            updateRange(
                              index,
                              "start_time",
                              event
                                .target
                                .value
                            )
                          }
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />

                        <TextField
                          fullWidth
                          type="time"
                          label="Hasta"
                          value={
                            range.end_time
                          }
                          onChange={(
                            event
                          ) =>
                            updateRange(
                              index,
                              "end_time",
                              event
                                .target
                                .value
                            )
                          }
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />

                        <IconButton
                          onClick={() =>
                            removeRange(
                              index
                            )
                          }
                          sx={{
                            border:
                              "1px solid #fecaca",
                            borderRadius:
                              2,
                            color:
                              "#b91c1c",
                            backgroundColor:
                              "#fff",
                            "&:hover": {
                              backgroundColor:
                                "#fef2f2",
                            },
                          }}
                        >
                          <DeleteOutlineRoundedIcon />
                        </IconButton>
                      </Box>
                    )
                  )}
                </Stack>
              )}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            paddingX: {
              xs: 2,
              sm: 3,
            },
            paddingBottom: 2,
          }}
        >
          <Button
            onClick={onClose}
            disabled={saving}
            color="inherit"
            sx={{
              fontWeight: 700,
            }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={onSave}
            disabled={saving}
            sx={{
              fontWeight: 700,
            }}
          >
            {saving ? (
              <CircularProgress
                size={20}
                sx={{
                  color: "#ffffff",
                }}
              />
            ) : form.id ? (
              "Guardar cambios"
            ) : (
              "Crear horario"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={feedback.open}
        autoHideDuration={
          feedback.severity ===
          "success"
            ? 1800
            : 3000
        }
        onClose={closeFeedback}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={
            feedback.severity
          }
          onClose={closeFeedback}
          sx={{
            width: "100%",
            fontWeight: 600,
          }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </>
  );
}